import * as turf from "@turf/turf"

import { getPreviousSegmentsTotalLength } from "@l2r-front/l2r-geodata"

import {
    getSegmentCircleIntersection,
    computeBrushRadius,
} from "./geometryUtils"
import {
    mergeOverlappingRanges,
    subtractRangeFromRanges,
} from "./rangeUtils"

// Re-export for backward compatibility
export { getSegmentCircleIntersection as segmentInsideCircle }

export function initStateFromFeatures(state, features) {
    features.forEach(feature => {
        const existingParametricSegment = state.parametricSegments.find(
            p => feature.properties.featureIndex === p.featureIndex && feature.properties.segmentIndex === p.segmentIndex,
        )
        if (existingParametricSegment) {
            existingParametricSegment.parametrics.push([feature.properties.start, feature.properties.end])
        } else {
            state.parametricSegments.push({
                featureIndex: feature.properties.featureIndex,
                segmentIndex: feature.properties.segmentIndex,
                parametrics: [[feature.properties.start, feature.properties.end]],
            })
        }

        state.featureIds.push(feature.id)
    })
}

function mergeParametricsRanges(parametricsRanges, rangeToAdd) {
    return mergeOverlappingRanges([...parametricsRanges, rangeToAdd])
}

export function addParametrics(parametrics, currentParametrics) {
    parametrics.forEach(param => {
        const currentSegmentParam = currentParametrics.find(
            p => p.featureIndex === param.featureIndex && p.segmentIndex === param.segmentIndex,
        )
        if (!currentSegmentParam) {
            param.parametrics = [param.parametrics]
            currentParametrics.push(param)
        } else {
            currentSegmentParam.parametrics = mergeParametricsRanges(currentSegmentParam.parametrics, param.parametrics)
        }
    })

    return currentParametrics
}

export function substractParametrics(parametrics, currentParametrics) {
    parametrics.forEach(param => {
        const currentSegmentParamIndex = currentParametrics.findIndex(
            p => p.featureIndex === param.featureIndex && p.segmentIndex === param.segmentIndex,
        )
        const currentSegmentParam = currentParametrics[currentSegmentParamIndex]
        if (currentSegmentParam) {
            const cuttedParametrics = subtractRangeFromRanges(currentSegmentParam.parametrics, param.parametrics)
            if (cuttedParametrics.length) {
                currentSegmentParam.parametrics = cuttedParametrics
            } else {
                currentParametrics.splice(currentSegmentParamIndex, 1)
            }
        }
    })

    return currentParametrics
}

export function createParametrics(mode, state, paintedParametrics, addition = true) {
    let featuresUpdated = false

    const newParametrics = addition
        ? addParametrics(paintedParametrics, state.parametricSegments)
        : substractParametrics(paintedParametrics, state.parametricSegments)

    state.parametricSegments = newParametrics

    const parametricsToCreate = []
    const featuresToKeep = []

    newParametrics.forEach(parametric => {
        parametric.parametrics.forEach(p => {
            const matchingFeatureId = state.featureIds.find(featureId => {
                const feature = mode.getFeature(featureId)
                return (
                    feature &&
                    feature.properties.featureIndex === parametric.featureIndex &&
                    feature.properties.segmentIndex === parametric.segmentIndex &&
                    feature.properties.start === p[0] &&
                    feature.properties.end === p[1]
                )
            })

            if (matchingFeatureId) {
                featuresToKeep.push(matchingFeatureId)
            } else {
                parametricsToCreate.push({
                    ...parametric,
                    parametrics: [p],
                })
            }
        })
    })

    state.featureIds.forEach(featureId => {
        if (!featuresToKeep.includes(featureId)) {
            mode.deleteFeature(featureId)
            featuresUpdated = true
        }
    })
    state.featureIds = [...featuresToKeep]

    parametricsToCreate.forEach(ps => {
        const feature = state.options.roadSegments.features[ps.featureIndex]
        const featureSegmentStart = feature.geometry.coordinates[ps.segmentIndex]
        const featureSegmentEnd = feature.geometry.coordinates[ps.segmentIndex + 1]
        const dx = featureSegmentEnd[0] - featureSegmentStart[0]
        const dy = featureSegmentEnd[1] - featureSegmentStart[1]

        const geoJSONFeatures = ps.parametrics.map(p => {
            const coordinates = [
                [featureSegmentStart[0] + p[0] * dx, featureSegmentStart[1] + p[0] * dy],
                [featureSegmentStart[0] + p[1] * dx, featureSegmentStart[1] + p[1] * dy],
            ]
            return turf.lineString(coordinates, {
                featureIndex: ps.featureIndex,
                segmentIndex: ps.segmentIndex,
                start: p[0],
                end: p[1],
            })
        })

        geoJSONFeatures.forEach(geoJSONFeature => {
            const drawFeature = mode.newFeature(geoJSONFeature)
            mode.addFeature(drawFeature)
            state.featureIds.push(drawFeature.id)
            featuresUpdated = true
        })
    })

    return featuresUpdated
}

export function initStateFromStartingFeatures(state) {
    state.parametricSegments = []
    state.featureIds = []

    if (!state.options.roadSegments) {
        return []
    }

    const paintedParametrics = []

    turf.segmentEach(state.options.roadSegments, (currentSegment, featureIndex, multiFeatureIndex, geometryIndex, segmentIndex) => {
        const roadFeature = state.options.roadSegments.features[featureIndex]
        const roadLength = turf.length(roadFeature)
        const segmentRelativeStart = getPreviousSegmentsTotalLength(roadFeature, segmentIndex) / roadLength
        const segmentRelativeEnd = segmentRelativeStart + turf.length(currentSegment) / roadLength

        const roadLinearLocation = roadFeature.linear_location[0]
        const linearRange = roadLinearLocation.end - roadLinearLocation.start

        const currentSegmentLinearLocation = {
            start: roadLinearLocation.start + segmentRelativeStart * linearRange,
            end: roadLinearLocation.start + segmentRelativeEnd * linearRange,
        }

        if (state.startingFeatures) {
            for (const roadworkLinearLocation of state.startingFeatures.linear_location ?? state.startingFeatures.linearLocation) {
                const minLinearRange = roadworkLinearLocation.start < currentSegmentLinearLocation.start
                    ? roadworkLinearLocation
                    : currentSegmentLinearLocation
                const maxLinearRange = minLinearRange === roadworkLinearLocation
                    ? currentSegmentLinearLocation
                    : roadworkLinearLocation

                if (minLinearRange.end >= maxLinearRange.start) {
                    const linearIntersection = {
                        start: maxLinearRange.start,
                        end: Math.min(minLinearRange.end, maxLinearRange.end),
                    }

                    const segmentRange = currentSegmentLinearLocation.end - currentSegmentLinearLocation.start
                    paintedParametrics.push({
                        featureIndex,
                        multiFeatureIndex,
                        geometryIndex,
                        segmentIndex,
                        parametrics: [
                            (linearIntersection.start - currentSegmentLinearLocation.start) / segmentRange,
                            (linearIntersection.end - currentSegmentLinearLocation.start) / segmentRange,
                        ],
                    })
                }
            }
        }
    })

    return paintedParametrics
}

export function computeBrushRadiusAtMousePosition(mode, mouseEvent) {
    return computeBrushRadius(mode.map, mouseEvent)
}

export function computeBrushParametrics(state, brushRadius, mouseEvent) {
    const paintedParametrics = []
    const brushCenter = [mouseEvent.lngLat.lng, mouseEvent.lngLat.lat]

    turf.segmentEach(state.roadSegments, (currentSegment, featureIndex, multiFeatureIndex, geometryIndex, segmentIndex) => {
        const segmentCoords = currentSegment.geometry.coordinates
        const intersectedParametrics = getSegmentCircleIntersection(
            segmentCoords[0],
            segmentCoords[1],
            brushCenter,
            brushRadius,
        )

        if (intersectedParametrics.length) {
            paintedParametrics.push({
                featureIndex,
                multiFeatureIndex,
                geometryIndex,
                segmentIndex,
                parametrics: intersectedParametrics,
            })
        }
    })

    return paintedParametrics
}
